import { Link } from 'react-router';
import { useLocation, useNavigate } from 'react-router';
import {
  LayoutDashboard, Users, UsersRound, MessageSquare,
  Bell, LogOut, Menu, Receipt, X,
  ChevronRight, Tag, FileText, Smartphone, Shield, ScrollText, ClipboardList, Coins,
} from 'lucide-react';
import { useState } from 'react';
import { GlobalSearch } from '@/components/search/global-search';
import { FincurryLogo } from '@/components/brand/fincurry-logo';
import { ThemeToggle } from '@/components/theme/theme-toggle';
import { Toaster } from '@/components/feedback/toast';
import { adminFetch } from '@/api';

const navSections = [
  {
    label: 'Overview',
    items: [
      { href: '/admin', label: 'Dashboard', icon: LayoutDashboard },
    ],
  },
  {
    label: 'Management',
    items: [
      { href: '/admin/users', label: 'Users', icon: Users },
      { href: '/admin/groups', label: 'Groups', icon: UsersRound },
      { href: '/admin/expenses', label: 'Expenses', icon: Receipt },
      { href: '/admin/categories', label: 'Categories', icon: Tag },
      { href: '/admin/currencies', label: 'Currencies', icon: Coins },
    ],
  },
  {
    label: 'Engagement',
    items: [
      { href: '/admin/feedback', label: 'Feedback', icon: MessageSquare },
      { href: '/admin/surveys', label: 'Surveys', icon: ClipboardList },
      { href: '/admin/notifications', label: 'Push Notify', icon: Bell },
    ],
  },
  {
    label: 'System',
    items: [
      { href: '/admin/pages', label: 'Pages', icon: FileText },
      { href: '/admin/app-config', label: 'App Config', icon: Smartphone },
      { href: '/admin/app-update', label: 'App Update', icon: Smartphone },
      { href: '/admin/settings/team', label: 'Team', icon: Shield },
      { href: '/admin/audit-log', label: 'Audit Log', icon: ScrollText },
    ],
  },
];

function Sidebar({ onNavigate }: { onNavigate?: () => void }) {
  const { pathname } = useLocation();
  const navigate = useNavigate();

  return (
    <div className="flex flex-col h-full w-[260px] border-r border-white/[0.06] bg-[#0a0a0b]">
      {/* Logo */}
      <div className="px-5 pt-6 pb-5">
        <div className="flex items-center gap-3">
          <FincurryLogo size="sm" />
          <div>
            <h1 className="text-[15px] font-semibold text-white tracking-tight">Fincurry</h1>
            <p className="text-[10px] text-zinc-500 font-medium tracking-widest uppercase">Admin Console</p>
          </div>
        </div>
      </div>

      {/* Nav */}
      <nav className="flex-1 px-3 overflow-y-auto space-y-5">
        {navSections.map(section => (
          <div key={section.label}>
            <p className="px-3 mb-1.5 text-[10px] font-semibold text-zinc-600 uppercase tracking-[0.08em]">
              {section.label}
            </p>
            <div className="space-y-0.5">
              {section.items.map(item => {
                const isActive =
                  pathname === item.href ||
                  (item.href !== '/admin' &&
                    item.href !== '/' &&
                    pathname.startsWith(item.href + '/'));
                return (
                  <Link
                    key={item.href}
                    to={item.href}
                    onClick={onNavigate}
                    className={`group flex items-center gap-3 px-3 py-2 rounded-lg text-[13px] font-medium transition-all duration-150 ${
                      isActive
                        ? 'bg-emerald-500/10 text-emerald-400 shadow-[inset_0_0_0_1px_rgba(52,211,153,0.1)]'
                        : 'text-zinc-500 hover:text-zinc-200 hover:bg-white/[0.04]'
                    }`}
                  >
                    <item.icon className={`w-4 h-4 flex-shrink-0 ${isActive ? 'text-emerald-400' : 'text-zinc-600 group-hover:text-zinc-400'}`} />
                    {item.label}
                    {isActive && (
                      <ChevronRight className="w-3 h-3 ml-auto text-emerald-400/50" />
                    )}
                  </Link>
                );
              })}
            </div>
          </div>
        ))}
      </nav>

      {/* Footer */}
      <div className="px-3 pb-4 pt-2 border-t border-white/[0.04] space-y-1">
        <div className="px-1">
          <ThemeToggle className="w-full justify-center" />
        </div>
        <button
          onClick={async () => {
            await adminFetch('/api/auth', { method: 'DELETE' });
            navigate('/admin/login');
          }}
          className="flex items-center gap-3 px-3 py-2 rounded-lg text-[13px] font-medium text-zinc-600 hover:text-red-400 hover:bg-red-500/5 transition-all w-full"
        >
          <LogOut className="w-4 h-4" />
          Logout
        </button>
      </div>
    </div>
  );
}

export default function DashboardLayout({ children }: { children: React.ReactNode }) {
  const [open, setOpen] = useState(false);

  return (
    <div className="flex h-screen bg-[#09090b] overflow-hidden">
      {/* Desktop sidebar */}
      <div className="hidden md:flex">
        <Sidebar />
      </div>

      {/* Mobile overlay */}
      {open && (
        <div className="fixed inset-0 z-50 md:hidden">
          <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={() => setOpen(false)} />
          <div className="relative h-full w-[260px] animate-in slide-in-from-left duration-200">
            <Sidebar onNavigate={() => setOpen(false)} />
          </div>
          <button
            onClick={() => setOpen(false)}
            className="absolute top-4 right-4 p-2 rounded-full bg-white/10 text-white"
          >
            <X className="w-4 h-4" />
          </button>
        </div>
      )}

      {/* Main */}
      <div className="flex-1 flex flex-col overflow-hidden">
        {/* Top bar (mobile) */}
        <div className="md:hidden flex items-center gap-3 px-4 py-3 border-b border-white/[0.06]">
          <button onClick={() => setOpen(true)} className="p-1.5 rounded-lg hover:bg-white/5">
            <Menu className="w-5 h-5 text-zinc-400" />
          </button>
          <span className="text-sm font-semibold text-white">Fincurry Admin</span>
        </div>

        {/* Content */}
        <main className="flex-1 overflow-y-auto">
          <div className="max-w-[1400px] mx-auto px-6 md:px-10 py-8">
            {children}
          </div>
        </main>
      </div>

      <GlobalSearch />
      <Toaster position="top-right" richColors closeButton />
    </div>
  );
}
